// Las tipografías de un .zip — ADR-2026-10-20-un-zip-de-tipografias-se-sube-entero.
//
// Una familia se descarga como un .zip con un archivo por estilo (Regular, Bold, Italic…), y a
// veces además la versión variable. Al subir el .zip a Elementos de marca se sacan TODAS las
// tipografías y cada una entra como un elemento, igual que si se hubieran elegido sueltas.
// Lo demás (licencias, léeme, imágenes, la basura de macOS) se ignora.

import { unzip, type UnzipFileInfo, type Unzipped } from 'fflate';

/** Más que esto no es una familia sino una biblioteca entera. */
export const MAX_TAMANO_ZIP_DE_FUENTES = 60 * 1024 * 1024;
export const MAX_FUENTES_POR_ZIP = 40;
/** Una fuente que pesa más que esto no es una fuente (o es una colección que no sirve acá). */
const MAX_FUENTE_DESCOMPRIMIDA = 15 * 1024 * 1024;

const MIME_DE_FUENTE: Record<string, string> = {
  woff2: 'font/woff2',
  woff: 'font/woff',
  ttf: 'font/ttf',
  otf: 'font/otf',
};

export const esZip = (file: Pick<File, 'name' | 'type'>): boolean =>
  /\.zip$/i.test(file.name) || file.type === 'application/zip' || file.type === 'application/x-zip-compressed';

const nombreBase = (ruta: string) => ruta.slice(ruta.replace(/\\/g, '/').lastIndexOf('/') + 1);
const extension = (ruta: string) => ruta.slice(ruta.lastIndexOf('.') + 1).toLowerCase();

/** ¿Esta entrada del .zip es una tipografía? Fuera las carpetas, `__MACOSX/` y los `._…`. */
export function esFuenteDelZip(ruta: string): boolean {
  const normal = ruta.replace(/\\/g, '/');
  if (normal.endsWith('/') || normal.startsWith('__MACOSX/') || normal.includes('/__MACOSX/')) return false;
  const base = nombreBase(normal);
  if (!base || base.startsWith('._') || base.startsWith('.')) return false;
  return extension(base) in MIME_DE_FUENTE;
}

const descomprimir = (bytes: Uint8Array, filtro: (f: UnzipFileInfo) => boolean): Promise<Unzipped> =>
  new Promise((resolve, reject) => unzip(bytes, { filter: filtro }, (err, datos) => (err ? reject(err) : resolve(datos))));

/**
 * Devuelve las tipografías del .zip como `File`s (con su tipo, que el .zip no trae), en orden
 * alfabético y sin repetir nombre. `omitidas` cuenta las que no entraron por el tope o el peso.
 * Tira un Error con un mensaje para mostrar si el .zip no se puede leer o no tiene ninguna.
 */
export async function leerFuentesDeZip(zip: File): Promise<{ fuentes: File[]; omitidas: number }> {
  if (zip.size > MAX_TAMANO_ZIP_DE_FUENTES) {
    throw new Error(`«${zip.name}» pesa más de ${MAX_TAMANO_ZIP_DE_FUENTES / 1024 / 1024} MB.`);
  }
  const bytes = new Uint8Array(await zip.arrayBuffer());
  let omitidas = 0;
  let datos: Unzipped;
  try {
    datos = await descomprimir(bytes, (f) => {
      if (!esFuenteDelZip(f.name)) return false;
      if (f.originalSize > MAX_FUENTE_DESCOMPRIMIDA) {
        omitidas++;
        return false;
      }
      return true;
    });
  } catch (e) {
    console.error('[leerFuentesDeZip]', e);
    throw new Error(`No pudimos abrir «${zip.name}». ¿Está completo?`);
  }
  const vistas = new Set<string>();
  const fuentes: File[] = [];
  for (const ruta of Object.keys(datos).sort((a, b) => nombreBase(a).localeCompare(nombreBase(b)))) {
    const base = nombreBase(ruta);
    if (vistas.has(base.toLowerCase())) continue;
    vistas.add(base.toLowerCase());
    if (fuentes.length >= MAX_FUENTES_POR_ZIP) {
      omitidas++;
      continue;
    }
    fuentes.push(new File([datos[ruta]], base, { type: MIME_DE_FUENTE[extension(base)] }));
  }
  if (fuentes.length === 0) {
    throw new Error(`«${zip.name}» no tiene tipografías (WOFF2, WOFF, TTF u OTF).`);
  }
  return { fuentes, omitidas };
}
